import * as React from 'react';
import { forwardRef, ComponentProps, useMemo, type JSX } from 'react';
import { Spinner } from '../spinner';
import { cn } from '@restworld/utils-common';

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => {
  const { Icon, ariaLabel, onClick, size = 'md', variant = 'neutral', loading, wrapperStyle, ...restProps } = props;

  const iconSizeClasses = useMemo(() => (size === 'sm' ? '!h-5 !w-5' : '!h-6 !w-6'), [size]);

  return (
    <Wrapper wrapperStyle={wrapperStyle} size={size}>
      <StyledButton
        variant={variant}
        size={size}
        aria-label={ariaLabel}
        onClick={onClick}
        ref={ref}
        loading={loading}
        {...restProps}
      >
        {loading ? (
          <Spinner className={`flex justify-center items-center ${iconSizeClasses}`} color={variant} />
        ) : (
          <Icon className={iconSizeClasses} />
        )}
      </StyledButton>
    </Wrapper>
  );
});
export default IconButton;

export type IconComponentType = (props: ComponentProps<'svg'>) => React.ReactNode;
export interface IconButtonProps extends Omit<ComponentProps<'button'>, 'aria-label'> {
  Icon: IconComponentType;
  ariaLabel: string;
  disabled?: boolean;
  twStyle?: string;
  loading?: boolean;
  size?: IconButtonSizeType;
  variant?: IconButtonVariantType;
  wrapperStyle?: string;
  withBg?: boolean;
}
type IconButtonSizeType = 'sm' | 'md';
export type IconButtonVariantType = 'neutral' | 'success' | 'warning' | 'error' | 'restworld' | 'reversed';

const iconColorStyles: Record<IconButtonVariantType, string> = {
  success: 'text-green-700 hover:bg-green-700/10 active:bg-green-700/20',
  warning: 'text-orange-700 hover:bg-orange-700/10 active:bg-orange-700/20',
  error: 'text-red-700 hover:bg-red-700/10 active:bg-red-700/20',
  restworld: 'text-purple-700 hover:bg-purple-700/10 active:bg-purple-700/20',
  reversed: 'text-white bg-white/10 active:bg-white',
  neutral: 'text-neutral-700 hover:bg-neutral-900/10 active:bg-neutral-900/20',
};

const iconLoadingBgColorStyles: Record<IconButtonVariantType, string> = {
  success: 'bg-green-700/10 hover:bg-green-700/10',
  warning: 'bg-orange-700/10 hover:bg-orange-700/10',
  error: 'bg-red-700/10 hover:bg-red-700/10',
  restworld: 'bg-purple-700/10 hover:bg-purple-700/10',
  reversed: 'bg-white hover:bg-white',
  neutral: 'bg-neutral-700/10 hover:bg-neutral-900/10',
};
const iconBgStyles: Record<IconButtonVariantType, string> = {
  success: 'bg-green-50 hover:bg-green-100',
  warning: 'bg-orange-50 hover:bg-orange-100',
  error: 'bg-red-50 hover:bg-red-100',
  restworld: 'bg-purple-50 hover:bg-purple-100',
  reversed: 'bg-white hover:bg-white',
  neutral: 'bg-neutral-50 hover:bg-neutral-100',
};
const Wrapper = ({ size, wrapperStyle, className, ...props }: any) => (
  <span
    className={cn(
      'grid place-items-center',
      size === 'sm' ? 'h-[26px] w-[26px]' : 'h-[42px] w-[42px]',
      wrapperStyle,
      className
    )}
    {...props}
  />
);
const StyledButton = ({ size, variant, disabled, loading, withBg, twStyle, className, ...props }: any) => (
  <button
    className={cn(
      'border-none outline-none p-0 m-0 text-inherit hover:cursor-pointer grid place-items-center transition duration-200 ease-in-out focus-visible:outline-[4px] focus-visible:outline-red-200',
      size === 'sm'
        ? '!h-6 !w-6 rounded-[4px] active:h-[22px] active:w-[22px]'
        : '!h-10 !w-10 rounded-lg active:h-[38px] active:w-[38px]',
      iconColorStyles[variant],
      disabled || loading ? 'text-neutral-100 hover:bg-transparent hover:cursor-not-allowed' : '',
      loading && iconLoadingBgColorStyles[variant],
      withBg ? iconBgStyles[variant] : '',
      twStyle,
      className
    )}
    {...props}
  />
);
